/**
 * Active filters component.
 *
 * Displays currently active filter chips with remove functionality.
 * Shows all applied filters and provides options to remove individual
 * filters or clear all filters at once.
 *
 * @module ActiveFilters
 */

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ActiveFiltersProps {
  readonly filters: Array<{ key: string; label: string; value: string }>;
  readonly onRemoveFilter: (key: string) => void;
  readonly onClearAll: () => void;
  readonly className?: string;
}

/**
 * Component for displaying active filter chips with remove functionality
 */
export function ActiveFilters({
  filters,
  onRemoveFilter,
  onClearAll,
  className,
}: ActiveFiltersProps) {
  const { t } = useTranslation();

  if (filters.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className || ''}`}>
      <span className="text-sm text-muted-foreground">
        {t('partnerFinder.search.activeFilters', { defaultValue: 'Active filters:' })}
      </span>
      {filters.map((filter) => (
        <Badge key={filter.key} variant="secondary" className="flex items-center gap-1.5 py-1">
          <span>
            {filter.key === 'keyword'
              ? filter.value
              : `${filter.label}: ${filter.value}`}
          </span>
          <button
            onClick={() => onRemoveFilter(filter.key)}
            className="hover:bg-muted rounded-full p-0.5"
            aria-label={t('partnerFinder.search.removeFilterAria', {
              label: filter.label,
              defaultValue: `Remove ${filter.label} filter`,
            })}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      <Button
        variant="ghost"
        size="sm"
        onClick={onClearAll}
        className="h-auto py-1 px-2 text-xs"
      >
        {t('partnerFinder.buttons.clearAll', { defaultValue: 'Clear all' })}
      </Button>
    </div>
  );
}
